import { useState, useEffect, useRef } from "react";
import { apiRequest } from "../utils/api";
import { useAuthContext } from "../context/AuthContext"; 
import { useNavigate } from "react-router-dom";
import { useAlert } from '../components/AlertModal';
import CategoryModal from "../components/CategoryModal";
import StarEffect from "../components/StarEffect";
import "../styles/StockInForm.css";

const StockInForm = () => {
    const [jan, setJan] = useState("");
    const janRef = useRef("");
    const [storeId, setStoreId] = useState("");
    const [entries, setEntries] = useState([]);
    const [stockMap, setStockMap] = useState({});
    const [categories, setCategories] = useState([]);
    const [categoryModalOpen, setCategoryModalOpen] = useState(false);
    const [pendingProduct, setPendingProduct] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const inputRef = useRef();
    const guraRef = useRef();
    const quantityRefs = useRef([]);
    const isSubmittingRef = useRef(false);
    const [animIndex, setAnimIndex] = useState(null);
    const { user: userInfo, loading } = useAuthContext();
    const navigate = useNavigate();
    const { showAlert } = useAlert();
    const [httpErrorCount, setHttpErrorCount] = useState(0);

    useEffect(() => {
        if (userInfo?.store_id) {
            setStoreId(userInfo.store_id);
        }
    }, [userInfo]);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await apiRequest("inventory/categorys/", "GET");
                setCategories(response);
            } catch (error) {
                console.error("カテゴリー取得失敗:", error);
            }
        };
        fetchCategories();
    }, []);

    if (loading) return <div>読み込み中...</div>;

    const handleJanChange = (e) => {
        setJan(e.target.value);
        janRef.current = e.target.value;
    };

    const handleJanSubmit = async () => {
        const value = janRef.current;

        // ★ スキャン完了後に http を含む場合はクリアして終了
        if (value.toLowerCase().includes("http")) {
            setJan("");
            janRef.current = "";
            setHttpErrorCount(prev => prev + 1);   // エラー回数カウント
            return;
        }

        if (value.length < 8 || isSubmittingRef.current) return;

        isSubmittingRef.current = true;
        try {
            const response = await apiRequest(`inventory/jan/${value}/`, "GET");

            if (!response.category) {
                setPendingProduct({ response, jan: value });
                setCategoryModalOpen(true);
                return;
            }

            addEntry(response, value);
        } catch (error) {
            console.error("商品が見つかりません", error);
        } finally {
            isSubmittingRef.current = false;
        }
    };

    const addEntry = (response, jan) => {
        const stockForStore = response.stock[storeId] ?? 0;
        const label = stockForStore === 0;
        const sellPrice = response.sell_price[storeId] ?? 0;

        setEntries((prev) => {
            const existingIndex = prev.findIndex(entry => entry.inventory === response.id);
            let updated;
            if (existingIndex !== -1) {
                updated = prev.map((entry, i) =>
                    i === existingIndex
                        ? { ...entry, quantity: entry.quantity + 1 }
                        : entry
                );
                setAnimIndex(existingIndex);
            } else {
                updated = [...prev, {
                    jan: jan,
                    inventory: response.id,
                    productName: response.item_name,
                    quantity: 1,
                    label: label,
                    price: sellPrice
                }];
                setAnimIndex(prev.length);
            }
            return updated;
        });

        setStockMap((prev) => ({
            ...prev,
            [response.id]: response.stock,
        }));

        setJan("");
        janRef.current = "";
        setTimeout(() => inputRef.current?.focus(), 100);
    };

    const handleCategorySave = async (categoryId) => {
        try {
            await apiRequest(`inventory/inventory/${pendingProduct.response.id}/`, "PATCH", {
                category_id: categoryId,
            });
            setCategoryModalOpen(false);
            setTimeout(() => {
                janRef.current = pendingProduct.jan;
                handleJanSubmit();
            }, 100);
        } catch (error) {
            showAlert("カテゴリーの保存に失敗しました");
        }
    };

    const handleCategoryCancel = () => {
        setCategoryModalOpen(false);
    };

    const handleAddToLabel = async (id, price) => {
        try {
            await apiRequest(`inventory/add_to_label/`, "POST", { id, price });
        } catch (error) {
            console.error("Error:", error);
            showAlert("エラーが発生しました");
        }
    };

    const submitStoreInventoryUpdate = async (inventoryId, storeId, scaleSwitch) => {
        try {
            const inventory = await apiRequest(`inventory/inventory/${inventoryId}/`);
            const existing = inventory.store_inventory_set.find(inv => inv.store === storeId);
            const isNew = !existing;

            const endpoint = isNew
                ? "inventory/storeinventory/"
                : `inventory/storeinventory/${existing.id}/`;
            const method = isNew ? "POST" : "PATCH";

            const payload = {
                ...(isNew ? { inventory: inventoryId, store: storeId } : {}),
                scale_stock_switch: scaleSwitch,
                version_buy: existing?.version_buy ?? 1,
            };

            await apiRequest(endpoint, method, payload);
        } catch (error) {
            console.error("StoreInventory更新失敗:", error);
            throw error;
        }
    };

    const handleStockInAll = async () => {
        if (isSubmitting) return; // 🔒 連続クリック防止
        setIsSubmitting(true);
    
        try {
            for (const entry of entries) {
                await apiRequest("inventory/inventory_adjustments/", "POST", {
                    inventory: entry.inventory,
                    store_id: storeId,
                    quantity_change: entry.quantity,
                    reason: "仕入れ",
                });
    
                if (entry.label) {
                    await handleAddToLabel(entry.inventory, entry.price);
                }
                if (entry.scale) {
                    await submitStoreInventoryUpdate(entry.inventory, storeId, true);
                }
            }
    
            setEntries([]);
            
            // 入庫成功時に http誤読カウントをリセット
            setHttpErrorCount(0);

            showAlert("すべての商品の入庫が完了しました");
            sessionStorage.setItem("returnToPriceLabel", "true");
            navigate('/inventory_list');
        } catch (error) {
            console.error("一括入庫処理中にエラー", error);
            showAlert("入庫処理中にエラーが発生しました");
            setIsSubmitting(false);  // 🛠️ エラーが起きたら再度押せるように
        }
    };

    return (
        <div className="StockInForm-container">
          
            <div className="StockInForm-header">
                <div className="StockInForm-header-box">
                    <h2 className="StockInForm-title">まとめて入庫</h2>
                        <div className="StockInForm-jan-row">
                            <label htmlFor="jan-input">JANコード:</label>
                            <input
                                id="jan-input"
                                type="text"
                                value={jan}
                                onChange={handleJanChange}
                                onKeyUp={(e) => {
                                    if (e.key === "Enter") handleJanSubmit();
                                }}
                                ref={inputRef}
                                autoFocus
                                className="StockInForm-jan-input"
                            />
                            <button className="StockInForm-scan-button" onClick={handleJanSubmit}>
                                SCAN
                            </button>
                        </div> 
                        <div>
                            {httpErrorCount > 0 && (
                                <div style={{ color: "#ffd700", marginLeft: "100px"}}>誤読エラー: {httpErrorCount} 回</div>
                            )}
                        </div>
                </div>
                <div className="gurabox" ref={guraRef}>
                    <img src="/media/system_images/gura01.png" alt="サメ" />
                    <div className="star-orbit">
                        <span className="star" style={{ transform: "rotateY(0deg) translateZ(70px)" }}>🔥</span>
                        <span className="star" style={{ transform: "rotateY(40deg) translateZ(70px)" }}>🔥</span>
                        <span className="star" style={{ transform: "rotateY(80deg) translateZ(70px)" }}>🔥</span>
                        <span className="star" style={{ transform: "rotateY(120deg) translateZ(70px)" }}>🔥</span>
                        <span className="star" style={{ transform: "rotateY(160deg) translateZ(70px)" }}>🔥</span>
                        <span className="star" style={{ transform: "rotateY(200deg) translateZ(70px)" }}>🔥</span>
                        <span className="star" style={{ transform: "rotateY(240deg) translateZ(70px)" }}>🔥</span>
                        <span className="star" style={{ transform: "rotateY(280deg) translateZ(70px)" }}>🔥</span>
                        <span className="star" style={{ transform: "rotateY(320deg) translateZ(70px)" }}>🔥</span>
                    </div>
                </div>                
            </div>

            
            <div className="StockInForm-list-area">
                <table className="StockInForm-table">
                    <thead>
                        <tr>
                            <th>ラベル</th>
                            <th>JANコード</th>
                            <th>商品名</th>
                            <th>現在在庫</th>
                            <th>数量</th>
                            <th>封印</th>
                            <th>削除</th>
                        </tr>
                    </thead>
                    <tbody>
                        {entries.map((entry, index) => {
                            const currentStock =
                                storeId && stockMap[entry.inventory]
                                    ? stockMap[entry.inventory][storeId] ?? "-"
                                    : "-";
                            return (
                                <tr key={index}>
                                    <td>
                                        <label className="StockInForm-toggle">
                                            <input
                                                type="checkbox"
                                                checked={entry.label}
                                                onChange={(e) => {
                                                    setEntries((prev) =>
                                                        prev.map((item, i) =>
                                                            i === index
                                                                ? { ...item, label: e.target.checked }
                                                                : item
                                                        )
                                                    );
                                                }}
                                            />
                                            <span className="StockInForm-toggle-slider"></span>
                                        </label>
                                    </td>
                                    <td>{entry.jan}</td>
                                    <td className="StockInForm-productName">{entry.productName}</td>
                                    <td>{currentStock}</td>
                                    <td>
                                        <input
                                            ref={(el) => (quantityRefs.current[index] = el)}
                                            type="number"
                                            className="StockInForm-quantity-input"
                                            value={entry.quantity}
                                            min={1}
                                            onChange={(e) => {
                                                const newQty = Math.max(1, Number(e.target.value));
                                                setEntries((prev) =>
                                                    prev.map((item, i) =>
                                                        i === index
                                                            ? { ...item, quantity: newQty }
                                                            : item
                                                    )
                                                );
                                            }}
                                        />
                                    </td>
                                    <td>
                                        <label className="StockInForm-toggle">
                                            <input
                                                type="checkbox"
                                                checked={entry.scale}
                                                onChange={(e) => {
                                                    setEntries((prev) =>
                                                        prev.map((item, i) =>
                                                            i === index
                                                                ? { ...item, scale: e.target.checked }
                                                                : item
                                                        )
                                                    );
                                                }}
                                            />
                                            <span className="StockInForm-toggle-slider"></span>
                                        </label>
                                    </td>
                                    <td>
                                        <button
                                            className="StockInForm-delete-button"
                                            onClick={() => {
                                                setEntries((prev) =>
                                                    prev.filter((_, i) => i !== index)
                                                );
                                            }}
                                        >
                                            ✖
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <button
                className="StockInForm-stockin-button"
                onClick={handleStockInAll}
                disabled={entries.length === 0 || isSubmitting}  // 🛑 連続クリック防止に対応
            >
                まとめて入庫
            </button>

            {animIndex !== null && (
                <StarEffect
                fromRef={guraRef}
                toRef={{ current: quantityRefs.current[animIndex] }}
                onEnd={() => setAnimIndex(null)}
                />
            )}

            <CategoryModal
                isOpen={categoryModalOpen}
                onSave={handleCategorySave}
                onCancel={handleCategoryCancel}
                categories={categories}
            />
        </div>
    );
};

export default StockInForm;